/** Vue 平台 Store → CDP iframe 编辑器的数据同步桥 */

import { formatEditorFontOption } from '@/utils/fontMapper';

export const EDITOR_DATA_SYNC_EVENT = 'jx-editor-data-sync';

const NON_CLONEABLE_KEYS = new Set([
  'blob',
  'pendingFile',
  'file',
  'state',
  'buffer',
  'arrayBuffer',
  '__v_isRef',
  '__v_isReactive',
  '__v_isReadonly',
  '__v_raw',
  'dep',
  'deps',
  'subs',
]);

const EDITOR_BOOTSTRAP_META_KEYS = [
  'id',
  'storeId',
  'templateId',
  'fromTemplate',
  'templateName',
  'thumb',
  'layerJson',
  'layerJsonRef',
  'editMasterTemplate',
  'isNew',
  'mode',
  'orderId',
  'fromTaskId',
  'taskId',
  'designId',
  'standalone',
  'fromTemplateLibrary',
  'skipAutoTemplate',
  'folderId',
  'departmentId',
  'departmentName',
  'printConfig',
  'skuId',
  'skuName',
  'skuConfirmed',
  'requiresSkuConfirmation',
  'requiresTemplateSelection',
  'returnView',
  'returnRoute',
  'platformSessionStoreId',
  'taskSkus',
  'ownerId',
  'ownerRole',
];

function isCloneBlockedValue(value) {
  if (value == null) return false;
  const type = typeof value;
  if (type === 'function' || type === 'symbol' || type === 'bigint') return true;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (typeof Node !== 'undefined' && value instanceof Node) return true;
  if (typeof Error !== 'undefined' && value instanceof Error) return true;
  return false;
}

/** postMessage 只能传递 structured-clone 安全的数据，需剔除 Blob/File/函数等 */
export function cloneForPostMessage(value, seen = null) {
  if (value == null) return value;
  if (isCloneBlockedValue(value)) return undefined;

  if (!seen) {
    try {
      return JSON.parse(JSON.stringify(value, (key, item) => {
        if (NON_CLONEABLE_KEYS.has(key)) return undefined;
        if (isCloneBlockedValue(item)) return undefined;
        return item;
      }));
    } catch {
      seen = new WeakSet();
    }
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return undefined;
    seen.add(value);
    return value
      .map(item => cloneForPostMessage(item, seen))
      .filter(item => item !== undefined);
  }

  if (typeof value === 'object') {
    if (seen.has(value)) return undefined;
    seen.add(value);
    const next = {};
    Object.entries(value).forEach(([key, item]) => {
      if (NON_CLONEABLE_KEYS.has(key)) return;
      const sanitized = cloneForPostMessage(item, seen);
      if (sanitized !== undefined) next[key] = sanitized;
    });
    return next;
  }

  return value;
}

export function slimFontForEditor(font = {}) {
  return {
    id: font.id,
    name: formatEditorFontOption(font),
    family: font.family,
    fileName: font.fileName,
    fileUrl: font.fileUrl || '',
    dataUrl: font.dataUrl,
    source: font.source,
    weight: font.weight || 400,
    style: font.style || 'normal',
  };
}

/** 模版列表同步：只传元数据，layerJson 由 bootstrap 单独传递 */
export function slimTemplateForEditorSync(t = {}) {
  return {
    id: t.id,
    storeId: t.storeId,
    name: t.name,
    type: t.type,
    scope: t.scope,
    dieCutType: t.dieCutType,
    widthMm: t.widthMm,
    heightMm: t.heightMm,
    bleedMm: t.bleedMm,
    thumb: t.thumb || t.coverThumbUrl || '',
    coverThumbUrl: t.coverThumbUrl || '',
    originFileUrl: t.originFileUrl,
    folderId: t.folderId,
    tags: Array.isArray(t.tags) ? [...t.tags] : [],
    pageCount: t.pageCount,
    useCount: t.useCount,
    uploaded: t.uploaded,
    updated: t.updated,
  };
}

export function slimMaterialForEditorSync(m = {}) {
  return {
    id: m.id,
    storeId: m.storeId,
    name: m.name,
    category: m.category,
    url: m.url,
    thumb: m.thumb,
    folderId: m.folderId,
    uploadedAt: m.uploadedAt,
  };
}

export function slimTaskSkuForEditor(sku = {}) {
  return {
    id: sku.id,
    name: sku.name,
    skuName: sku.skuName,
    spec: sku.spec,
    material: sku.material,
    size: sku.size,
    quantity: sku.quantity,
  };
}

export function slimOrganizationTreeForEditor(tree = null) {
  if (!tree || typeof tree !== 'object') return null;
  return {
    id: String(tree.id || ''),
    name: tree.name || '',
    totalStores: Number(tree.totalStores) || 0,
    groups: (tree.groups || []).map(group => ({
      id: String(group.id || ''),
      name: group.name || '',
      enabled: group.enabled !== false,
      storeCount: Number(group.storeCount) || 0,
      stores: (group.stores || []).map(store => ({
        id: store.id,
        name: store.name || store.departmentName || store.id,
        departmentId: store.departmentId,
        departmentName: store.departmentName,
        parentId: store.parentId,
        groupId: store.groupId,
        groupName: store.groupName,
        enabled: store.enabled !== false,
      })),
      users: (group.users || []).map(user => ({
        id: user.id != null ? String(user.id) : '',
        name: user.name || '',
        mobile: user.mobile || '',
        email: user.email || '',
        role: user.role || '',
        position: user.position || '',
      })),
    })),
  };
}

export function pickEditorBootstrapMeta(meta = {}) {
  const picked = {};
  EDITOR_BOOTSTRAP_META_KEYS.forEach((key) => {
    if (meta[key] !== undefined) picked[key] = meta[key];
  });
  if (Array.isArray(picked.taskSkus)) {
    picked.taskSkus = picked.taskSkus.map(slimTaskSkuForEditor);
  }
  if (picked.printConfig && typeof picked.printConfig === 'object') {
    const { global, stores } = picked.printConfig;
    picked.printConfig = {
      global: global ? { ...global } : {},
      stores: stores ? { ...stores } : {},
    };
  }
  if (picked.layerJson != null && typeof picked.layerJson !== 'string') {
    try {
      picked.layerJson = JSON.stringify(picked.layerJson);
    } catch {
      picked.layerJson = '';
    }
  }
  return picked;
}

export function buildEditorSyncPayload({
  materials = [],
  materialFolders = {},
  platformFonts = [],
  useRemoteMaterials = false,
  deletedDefaultMaterialIds = [],
  templates = [],
  templateFolders = {},
  useRemoteTemplates = false,
  useRemoteFonts = false,
  useRemoteDesignCenter = false,
  stores = [],
  organizationTree = null,
  currentStoreId = '',
} = {}) {
  return cloneForPostMessage({
    materials: materials.map(slimMaterialForEditorSync),
    materialFolders: materialFolders || {},
    useRemoteMaterials: Boolean(useRemoteMaterials),
    deletedDefaultMaterialIds: [...(deletedDefaultMaterialIds || [])],
    templates: templates.map(slimTemplateForEditorSync),
    templateFolders: templateFolders || {},
    useRemoteTemplates: Boolean(useRemoteTemplates),
    useRemoteFonts: Boolean(useRemoteFonts),
    useRemoteDesignCenter: Boolean(useRemoteDesignCenter),
    fonts: platformFonts.map(slimFontForEditor),
    stores: stores.map(s => ({
      id: s.id,
      name: s.name || s.departmentName || s.id,
      departmentId: s.departmentId,
      departmentName: s.departmentName,
      parentId: s.parentId,
    })),
    organizationTree: slimOrganizationTreeForEditor(organizationTree),
    currentStoreId: currentStoreId || '',
  });
}

export function notifyEditorDataSync(payload) {
  if (typeof window === 'undefined' || !payload) return;
  window.dispatchEvent(new CustomEvent(EDITOR_DATA_SYNC_EVENT, { detail: payload }));
}
